import { useCallback, useEffect, useRef, useState } from "react";

export function appendDictationText(existingText, transcript) {
  const existing = String(existingText || "");
  const spoken = String(transcript || "").trim();
  if (!spoken) return existing;
  if (!existing.trim()) return spoken;
  return `${existing}${/\s$/.test(existing) ? "" : " "}${spoken}`;
}

function getSpeechRecognitionConstructor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function speechErrorMessage(error) {
  switch (error) {
    case "not-allowed":
    case "service-not-allowed":
      return "Permita o uso do microfone no navegador para ditar.";
    case "no-speech":
      return "Nenhuma fala foi identificada. Tente novamente.";
    case "audio-capture":
      return "Não foi possível acessar o microfone neste dispositivo.";
    case "network":
      return "O navegador não conseguiu transcrever a fala agora.";
    default:
      return "Não foi possível iniciar o ditado. Tente novamente.";
  }
}

export function VoiceDictationButton({ value, onChange, disabled = false }) {
  const SpeechRecognition = getSpeechRecognitionConstructor();
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef(null);
  const valueRef = useRef(value || "");
  const finalTranscriptRef = useRef("");
  const interimTranscriptRef = useRef("");
  const cancelledRef = useRef(false);
  const recognitionErrorRef = useRef(false);

  useEffect(() => {
    valueRef.current = value || "";
  }, [value]);

  const resetDictation = useCallback(() => {
    recognitionRef.current = null;
    finalTranscriptRef.current = "";
    interimTranscriptRef.current = "";
    setInterimTranscript("");
  }, []);

  const finishDictation = useCallback(() => {
    const transcript = [
      finalTranscriptRef.current,
      interimTranscriptRef.current,
    ].filter(Boolean).join(" ").trim();
    if (!cancelledRef.current && !recognitionErrorRef.current && transcript) {
      onChange(appendDictationText(valueRef.current, transcript));
    }
    resetDictation();
    setStatus("idle");
  }, [onChange, resetDictation]);

  useEffect(() => () => {
    cancelledRef.current = true;
    if (recognitionRef.current) recognitionRef.current.abort();
  }, []);

  const startDictation = () => {
    if (disabled || status === "starting" || status === "listening") return;
    if (!SpeechRecognition) {
      setStatus("unsupported");
      setMessage("Seu navegador não oferece ditado por voz. Você pode digitar normalmente.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "pt-BR";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    cancelledRef.current = false;
    recognitionErrorRef.current = false;
    finalTranscriptRef.current = "";
    interimTranscriptRef.current = "";
    setInterimTranscript("");
    setMessage("");
    recognitionRef.current = recognition;

    recognition.onstart = () => setStatus("listening");
    recognition.onresult = event => {
      let finalText = finalTranscriptRef.current;
      let currentInterim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const text = result[0]?.transcript || "";
        if (result.isFinal) finalText += text;
        else currentInterim += text;
      }
      finalTranscriptRef.current = finalText;
      interimTranscriptRef.current = currentInterim;
      setInterimTranscript(currentInterim);
    };
    recognition.onerror = event => {
      recognitionErrorRef.current = true;
      setStatus("error");
      setMessage(speechErrorMessage(event.error));
    };
    recognition.onend = () => {
      finishDictation();
    };

    try {
      recognition.start();
      setStatus("starting");
    } catch (error) {
      recognitionErrorRef.current = true;
      recognitionRef.current = null;
      setStatus("error");
      setMessage("Não foi possível iniciar o ditado. Tente novamente.");
    }
  };

  const stopDictation = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
  };

  const cancelDictation = () => {
    cancelledRef.current = true;
    if (recognitionRef.current) recognitionRef.current.abort();
    resetDictation();
    setStatus("idle");
    setMessage("");
  };

  const isListening = status === "starting" || status === "listening";
  const statusText = isListening
    ? interimTranscript
      ? `Ouvindo: “${interimTranscript.trim()}”`
      : "Gravando — fale agora"
    : message;

  return (
    <div style={{ marginTop: 7 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={isListening ? stopDictation : startDictation}
          disabled={disabled}
          aria-label={isListening ? "Parar ditado e inserir texto" : "Ditar observação por voz"}
          style={{
            border: `1px solid ${isListening ? "#DC2626" : "#CBD5E1"}`,
            background: isListening ? "#FEF2F2" : "#F8FAFC",
            color: isListening ? "#B91C1C" : "#334155",
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 12,
            fontWeight: 700,
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {isListening ? "⏹ Parar e inserir" : "🎙️ Ditar por voz"}
        </button>
        {isListening && (
          <button
            type="button"
            onClick={cancelDictation}
            aria-label="Cancelar ditado"
            style={{
              border: "1px solid #CBD5E1",
              background: "#fff",
              color: "#475569",
              borderRadius: 8,
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        )}
      </div>
      {statusText && (
        <div
          role="status"
          aria-live="polite"
          style={{
            color: isListening ? "#B91C1C" : "#64748B",
            fontSize: 11,
            fontWeight: isListening ? 700 : 500,
            marginTop: 5,
          }}
        >
          {isListening && "🔴 "}{statusText}
        </div>
      )}
    </div>
  );
}